/* storybook-check-ignore */
import {
  ExclusiveTourRequest,
  ExclusiveTourRequestTimeSlot,
  REQUEST_TOUR_ERROR_MESSAGES,
  SLOT_DATE_TIME_FORMAT,
  SLOT_TIME_FORMAT,
} from './util';
import { useMemo, useRef, UIEventHandler, useState, useEffect } from 'react';
import { Text, Flex, Loader, Button, Icon } from '@opendoor/bricks/core';
import RequestATourSupportSection from './RequestATourSupportSection';
import moment from 'moment-timezone';
import { ListingWithComputedProperties } from 'declarations/exclusives/listing';
import GalleryGrid from 'components/exclusives/gallery/GalleryGrid';
import debounce from 'lodash/debounce';
import { RequestATourError, useRequestATour } from './useRequestATour';
import Head from 'next/head';

interface Props
  extends Pick<
    ReturnType<typeof useRequestATour>,
    | 'createTourRequest'
    | 'isInstantSlot'
    | 'tourableListings'
    | 'instantSlots'
    | 'slots'
    | 'canCreateTourRequest'
  > {
  isTourRequestLoading: boolean;
  isTourRequestError: boolean;
  tourRequestData: ExclusiveTourRequest | null | undefined;
  error?: RequestATourError;
  onError?: () => void;
  selectedSlots: ExclusiveTourRequestTimeSlot[];
}
export default function ReserveATourConfirmation(props: Props) {
  const { selectedSlots, hasSelectedInstantSlots } = useMemo(() => {
    const selectedInstantSlots = props.selectedSlots.filter(props.isInstantSlot);
    const hasSelectedInstantSlots = selectedInstantSlots.length > 0;
    return {
      hasSelectedInstantSlots,
      selectedSlots: hasSelectedInstantSlots ? selectedInstantSlots : props.selectedSlots,
    };
  }, [props]);
  useEffect(() => {
    props.createTourRequest().then((data) => {
      if (data === undefined) {
        props.onError?.();
      }
    });
  }, []);

  return (
    <>
      <Text fontSize="s4" fontWeight="bold">
        {hasSelectedInstantSlots
          ? 'Your tour request is confirmed'
          : 'Your tour request is being processed'}
      </Text>
      <Text fontSize="s2">
        {hasSelectedInstantSlots
          ? 'Check your email and text for next steps.'
          : 'We’ll confirm with the seller if these times work for them. Check your email and text for next steps.'}
      </Text>
      <Text fontSize="s2" fontWeight="semibold">
        {hasSelectedInstantSlots ? 'Confirmed date/time' : 'Requested date/times'}
      </Text>
      <Flex
        borderRadius="roundedSquare"
        paddingX="1rem"
        paddingY="0.75rem"
        backgroundColor="neutrals10"
        border="1px solid"
        borderColor="neutrals40"
        flexDirection="column"
      >
        {props.isTourRequestLoading && (
          <Flex width="100%" alignItems="center" height="7.5rem">
            <Loader />
          </Flex>
        )}
        {!props.isTourRequestLoading &&
          !props.isTourRequestError &&
          selectedSlots.map((slot, i) => (
            <Text key={`slotConfirmationTimeSlotInfo${i}`} fontSize="s1">
              {`${moment(slot.start_time).format(SLOT_DATE_TIME_FORMAT)} - ${moment(
                slot.end_time,
              ).format(SLOT_TIME_FORMAT)}`}
            </Text>
          ))}
        {props.isTourRequestError && (
          <Text color="red50">
            {(props.error && REQUEST_TOUR_ERROR_MESSAGES[props.error.type]) || 'A error ocurred!'}
          </Text>
        )}
      </Flex>
      <RequestATourSupportSection title="Want to edit or cancel?" />
      {!!props.tourableListings.length && (
        <PeopleAreAlsoTouringTheseHomes listings={props.tourableListings} />
      )}
      <Flex marginBottom="1rem" />
    </>
  );
}

function PeopleAreAlsoTouringTheseHomes({
  listings,
}: {
  listings: ListingWithComputedProperties[];
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [disableScroll, setDisableScroll] = useState({ left: true, right: false });
  const onScroll = useMemo<UIEventHandler<HTMLDivElement>>(() => {
    const callback: UIEventHandler<HTMLDivElement> = () => {
      if (ref.current) {
        const { scrollLeft, clientWidth, scrollWidth } = ref.current;
        setDisableScroll({
          left: scrollLeft == 0,
          right: scrollLeft + clientWidth == scrollWidth,
        });
      }
    };
    return debounce(callback, 100);
  }, [setDisableScroll]);
  function scrollGrid(to: 'left' | 'right') {
    if (ref.current) {
      ref.current.scrollBy({
        left: (to === 'left' ? -1 : 1) * ref.current.clientWidth,
        behavior: 'smooth',
      });
    }
  }
  return (
    <Flex flexDirection="column" gridGap="1rem" overflowX="visible">
      <Head>
        <link
          rel="stylesheet"
          type="text/css"
          href="https://cdn.jsdelivr.net/npm/react-toastify@4.5.1/dist/ReactToastify.min.css"
        />
      </Head>
      <Flex flexDirection="row" justifyContent="space-between">
        <Text fontSize="s2" fontWeight="semibold">
          People are also touring these homes
        </Text>
        <Flex flexDirection="row" gridGap="0.75rem" display={['none', 'flex', 'flex']}>
          <Button
            disabled={disableScroll.left}
            variant="icon"
            aria-label={null}
            analyticsName=""
            onClick={() => scrollGrid('left')}
          >
            <Icon name="arrow-left" size={16} />
          </Button>
          <Button
            disabled={disableScroll.right}
            variant="icon"
            aria-label={null}
            analyticsName=""
            onClick={() => scrollGrid('right')}
          >
            <Icon name="arrow-right" size={16} />
          </Button>
        </Flex>
      </Flex>
      <Flex overflowX="auto" ref={ref} onScroll={onScroll} paddingX="0.5rem">
        <GalleryGrid
          listings={listings}
          gridTemplateColumns={`repeat(${listings.length}, minmax(256px, 1fr))`}
          gridGap="1.5rem"
          containerProps={{ paddingBottom: '1rem' }}
        />
      </Flex>
    </Flex>
  );
}
